import { useState } from 'react';
import { Button, EmptyState, Modal, Skeleton } from '@/components/ui';
import { formatMoneyText } from '@/components/ui/Money';
import { Notice } from '@/components/ui/Notice';
import { useBillingRegionLabels } from '@/lib/billing-region';
import { sumDecimalStrings } from '@/lib/decimal';
import { formatDateOnly } from '@/lib/format';
import { conversionNote, excludedByCurrencyNote, targetMetricLabel } from '@/lib/targets';
import type { ApiError } from '@/lib/api';
import type { TargetActualRow, TargetActualRowsParams, TargetMetric } from '@/lib/types';
import { useDrilldownRows, type DrilldownRowsMeta, type DrilldownRowsSource } from '@/lib/use-target-actual-rows';
import { exportRowsToXlsx, type XlsxExportColumn } from '@/lib/xlsx-export';
import { columnsForMetric } from './columns';
import { exportDrilldownValue, type DrilldownCellContext } from './drilldown-columns-shared';
import { DrilldownTable } from './DrilldownTable';

/**
 * The documents behind one drill-down figure (owner 2026-10-28), opened from
 * University Partnerships "Targets & performance" and Management -> Financial
 * Analytics. One instance per page; a `null` source fetches nothing.
 *
 * Two sources (`source`, or the original `params` for a target tile):
 * - `targets`: params are EXACTLY what the panel sent to `/targets/actuals`,
 *   plus the metric, so the rows reconcile to the tile (owner decision 1).
 *   Rows the viewer cannot open are removed server-side and summarised from
 *   `meta.hidden` — a fact about the data, not helper text (decision 2). The
 *   subtitle carries the rates basis.
 * - `commission`: `/commissions/overview/rows` for one person's personal or
 *   regional figure on "Targets & commission". Shown with the invoiced
 *   metric's columns; no hidden rows and no rates sentence (document-date
 *   conversion only).
 *
 * - "Export to Excel" writes ALL visible rows and ALL columns, ignoring the
 *   on-screen filters and hidden columns (decision 3).
 * - Errors match `ApiError.code` before the generic message.
 */
export type MetricDrilldownSource = DrilldownRowsSource;

export interface MetricDrilldownModalProps {
  open: boolean;
  onClose: () => void;
  /** Wins over `params` when given (including `null`). */
  source?: MetricDrilldownSource | null;
  /** The original targets-only form: same as `source: { kind: 'targets', params }`. */
  params?: TargetActualRowsParams | null;
  title: string;
}

const COMMISSION_FIGURE_LABEL: Record<'personal' | 'regional', string> = {
  personal: 'Invoiced',
  regional: 'Regional invoiced',
};

function periodText(meta: DrilldownRowsMeta): string {
  return `${formatDateOnly(meta.window.from)} – ${formatDateOnly(meta.window.to)}`;
}

/** Targets only: the rates basis. Commission rows have no sentence. */
function ratesText(meta: DrilldownRowsMeta): string | null {
  if (meta.kind !== 'targets') return null;
  return conversionNote({
    fxMode: meta.fxMode,
    translation: meta.translation,
    currency: meta.measuredInCurrency,
  });
}

function sourceMetric(source: MetricDrilldownSource | null): TargetMetric | null {
  if (source === null) return null;
  return source.kind === 'targets' ? source.params.metric : 'invoiced';
}

function sheetLabel(source: MetricDrilldownSource, metric: TargetMetric): string {
  return source.kind === 'commission' ? COMMISSION_FIGURE_LABEL[source.params.figure] : targetMetricLabel(metric);
}

function fileStem(source: MetricDrilldownSource, metric: TargetMetric): string {
  return source.kind === 'commission' ? `commission-${source.params.figure}` : metric;
}

function errorText(error: ApiError): string {
  switch (error.code) {
    case 'FX_TRANSLATION_RATE_UNAVAILABLE':
      return 'No exchange rate is stored for the translation date of this period, so these documents cannot be converted at period end.';
    case 'TOO_MANY_ROWS':
      return 'More than 10,000 documents are behind this figure, which is too many to list.';
    default:
      return error.formMessage ?? error.message;
  }
}

export function MetricDrilldownModal({ open, onClose, source: sourceProp, params, title }: MetricDrilldownModalProps) {
  const source: MetricDrilldownSource | null =
    sourceProp !== undefined
      ? sourceProp
      : params !== undefined && params !== null
        ? { kind: 'targets', params }
        : null;
  const state = useDrilldownRows(open ? source : null);
  const billingRegionLabel = useBillingRegionLabels();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const meta = state.status === 'ready' ? state.meta : null;
  const metric = sourceMetric(source);
  const columns = metric !== null ? columnsForMetric(metric) : [];
  const ctx: DrilldownCellContext | null =
    meta !== null ? { measuredInCurrency: meta.measuredInCurrency, billingRegionLabel } : null;
  const hidden = meta !== null && meta.kind === 'targets' ? meta.hidden : null;
  const rates = meta !== null ? ratesText(meta) : null;

  async function handleExport() {
    if (meta === null || metric === null || ctx === null || source === null) return;
    const exportColumns: XlsxExportColumn<TargetActualRow>[] = columns.map((column) => ({
      key: column.key,
      label: column.key === 'convertedAmount' ? `${column.label} (${meta.measuredInCurrency})` : column.label,
      value: (row) => exportDrilldownValue(column, row, ctx),
      ...(column.numFmt !== undefined ? { numFmt: column.numFmt } : {}),
    }));
    const convertedTotal = sumDecimalStrings(
      state.rows.filter((row) => !row.excluded).map((row) => row.convertedAmount),
    );
    const label = sheetLabel(source, metric);
    setExporting(true);
    setExportError(null);
    try {
      await exportRowsToXlsx({
        fileName: `seed-emerge-${fileStem(source, metric)}-${meta.window.from}-${meta.window.to}.xlsx`,
        sheetName: label,
        title: [
          `${title} · ${label}`,
          periodText(meta),
          `Measured in ${meta.measuredInCurrency}`,
          ...(rates !== null ? [rates] : []),
        ],
        columns: exportColumns,
        rows: state.rows,
        ...(convertedTotal !== null ? { totals: { convertedAmount: convertedTotal } } : {}),
      });
    } catch {
      setExportError('The Excel file could not be created.');
    } finally {
      setExporting(false);
    }
  }

  const description =
    meta !== null ? (
      <span>
        {periodText(meta)} · {meta.measuredInCurrency}
        {rates !== null ? ` · ${rates}` : ''}
      </span>
    ) : undefined;

  const excludedNote = meta !== null ? excludedByCurrencyNote(meta.excludedCount) : null;
  const hiddenCount = hidden?.count ?? 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="xl"
      {...(description !== undefined ? { description } : {})}
      headerActions={
        <Button
          variant="secondary"
          size="sm"
          loading={exporting}
          disabled={meta === null || state.rows.length === 0}
          onClick={() => void handleExport()}
        >
          Export to Excel
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {exportError !== null && (
          <Notice tone="danger" role="alert">
            {exportError}
          </Notice>
        )}

        {(state.status === 'loading' || state.status === 'idle') && (
          <div className="flex flex-col gap-2" aria-busy="true">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        )}

        {state.status === 'error' && state.error !== null && (
          <Notice
            tone="danger"
            role="alert"
            action={
              <Button variant="secondary" size="sm" onClick={state.reload}>
                Retry
              </Button>
            }
          >
            {errorText(state.error)}
          </Notice>
        )}

        {meta !== null && metric !== null && ctx !== null && (
          <>
            {(hiddenCount > 0 || excludedNote !== null) && (
              <div className="flex flex-col gap-1 text-xs text-fg-muted">
                {hidden !== null && hiddenCount > 0 && (
                  <p>
                    {hiddenCount === 1 ? '1 row' : `${hiddenCount.toLocaleString('en-GB')} rows`} (
                    {formatMoneyText(hidden.amount, meta.measuredInCurrency)}){' '}
                    {hiddenCount === 1 ? 'is on a record' : 'are on records'} you cannot open.
                  </p>
                )}
                {excludedNote !== null && <p title={excludedNote.detail}>{excludedNote.short}</p>}
              </div>
            )}

            {state.rows.length === 0 ? (
              hiddenCount > 0 ? null : (
                <EmptyState title="No documents are behind this figure." />
              )
            ) : (
              <DrilldownTable metric={metric} rows={state.rows} columns={columns} ctx={ctx} />
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
